<template>
  <DropdownBubble
    :is-visible="display"
    :vertical-offset="verticalOffset"
    :px-wrapper-right="pxWrapperRight"
    :pointer-orientation="pointerOrientation"
    :px-pointer-position="pxPointerPosition"
    @close="$emit('close')"
  >
    <ul class="account-switcher">
      <template v-if="accessibles">
        <li
          v-if="accessibles.apollo.length && activeView"
          class="account-switcher__item whitebox-item"
        >
          <button
            class="unstyled-button account-switcher__link"
            :class="{
              'account-switcher__link--active': isEnterpriseView
            }"
            @click.stop="switchToDataSubs()"
          >
            <span class="sk-icon-data-source" aria-hidden="true"></span>
            {{ $t('common.labelDataSubs') }}
            <SvgIcon
              name="spinner"
              class="spinner-icon u-margin-left-2x"
              :class="{ 'show-icon': spinnerId === 'data-subs' }"
            />
          </button>
        </li>

        <li
          v-for="unit in accessibles.units"
          :key="unit.unitId"
          class="account-switcher__item whitebox-item"
        >
          <button
            class="unstyled-button account-switcher__link"
            :class="{
              'account-switcher__link--active':
                unit.unitId == activeView.data.unitId
            }"
            @click.stop="switchToWhitebox(unit)"
          >
            <span class="sk-icon-whitebox" aria-hidden="true"></span>
            {{ $t('pages.redirect.whiteboxNum', { id: unit.unitId }) }}
            <SvgIcon
              name="spinner"
              class="spinner-icon u-margin-left-2x"
              :class="{ 'show-icon': spinnerId === unit.unitId }"
            />
          </button>
        </li>
      </template>

      <hr v-if="activeView && activeView.type !== 'signup'" class="separator" />

      <li class="account-switcher__item">
        <button
          class="unstyled-button account-switcher__link"
          :class="{
            'account-switcher__link--updates': unreadUpdates && notify
          }"
          @click="$store.commit('featureUpdates/TOGGLE_IS_MODAL_VISIBLE')"
        >
          <span class="sk-icon-bell1" aria-hidden="true"></span>
          {{ $t('components.ecosystems.UpdatesModal.header') }}
        </button>
      </li>
      <li class="account-switcher__item">
        <router-link
          class="account-switcher__link"
          to="/settings/personal-details"
        >
          <span class="sk-icon-knowledge-base" aria-hidden="true"></span>
          {{ $t('navigation.personalDetails') }}
        </router-link>
      </li>
      <li class="account-switcher__item">
        <router-link
          v-if="isConsumerView && activeView.data.unitId"
          data-cy="account-switcher-broadband-details"
          class="account-switcher__link"
          :to="{ name: 'BroadbandDetails' }"
        >
          <span class="sk-icon-meany" aria-hidden="true"></span>
          {{ $t('navigation.broadbandDetails') }}
        </router-link>
      </li>
      <li class="account-switcher__item">
        <router-link class="account-switcher__link" to="/settings/contact">
          <span class="sk-icon-faqs" aria-hidden="true"></span>
          {{ $t('navigation.contactUs') }}
        </router-link>
      </li>
      <li class="account-switcher__item" data-cy="lang-switcher-link">
        <button
          class="unstyled-button account-switcher__link"
          @click="selectLanguageClicked"
        >
          <span class="sk-icon-comment" aria-hidden="true"></span>
          {{ $t('navigation.selectLanguage') }}
        </button>
      </li>
      <li class="account-switcher__item">
        <router-link class="account-switcher__link" to="/logout">
          <span class="sk-icon-right2" aria-hidden="true"></span>
          {{ $t('common.linkLogout') }}
        </router-link>
      </li>
    </ul>
  </DropdownBubble>
</template>

<script lang="ts">
import DropdownBubble from '@/common/components/DropdownBubble.vue';
import viewTypes, { VIEW_TYPE } from '@/common/helpers/view-types';
import AuthService, {
  AccessiblePanel,
  Accessibles
} from '@/common/services/Auth';
import { SvgIcon } from '@te/cdev-design-system';
import { prefStorage } from '@te/cdev-utils';
import { defineComponent } from 'vue';

export default defineComponent({
  components: {
    DropdownBubble,
    SvgIcon
  },
  props: {
    display: {
      type: Boolean,
      required: true
    },
    verticalOffset: {
      type: Object,
      required: true
    },
    pxWrapperRight: {
      type: String,
      required: true
    },
    pointerOrientation: {
      type: String,
      required: true
    },
    pxPointerPosition: {
      type: String,
      required: true
    }
  },
  data: () => ({
    activeView: viewTypes.activeView,
    isConsumerView: viewTypes.isConsumerView,
    isEnterpriseView: viewTypes.isEnterpriseView,
    accessibles: prefStorage.get<Accessibles>('accessibles'),
    spinnerId: 0 as string | number,
    notify: prefStorage.get<boolean>('notify-updates', true)
  }),
  computed: {
    unreadUpdates() {
      return this.$store.state.featureUpdates.newUpdates.length;
    }
  },
  methods: {
    showLanguageSwitcher() {
      this.$store.commit('languageSwitcher/SET_VISIBILITY', true);
    },
    switchToDataSubs() {
      this.spinnerId = 'data-subs';

      if (!prefStorage.get<AccessiblePanel>('panel')) {
        const sortedPanels = prefStorage
          .get<Accessibles>('accessibles')
          .apollo.sort((a, b) =>
            a.front_name.toLowerCase() > b.front_name.toLowerCase() ? 1 : -1
          );

        this.$store.commit('setPanel', sortedPanels[0]);
      }

      this.switchToView(VIEW_TYPE.ENTERPRISE, {});
    },
    switchToWhitebox(box) {
      this.spinnerId = box.unitId;
      this.switchToView('units', box);
    },
    switchToView(type, data) {
      AuthService.goToView(type, data);
    },
    selectLanguageClicked() {
      this.showLanguageSwitcher();
    }
  }
});
</script>

<style lang="scss" scoped>
ul {
  margin-bottom: 0;
  padding: 0;

  list-style: none;
}

.account-switcher {
  padding: 15px;
  max-height: calc(90vh - 150px);
  overflow-y: auto;
  -ms-overflow-style: none;
}

a {
  text-decoration: none;
}

[class^='sk-icon-'] {
  margin-right: 0.5em;

  [dir='rtl'] & {
    margin-right: 0;
    margin-left: 0.5em;
  }
}

.account-switcher__link {
  display: flex;

  padding: 8px;
  min-width: 200px;

  font-size: 1.2em;

  align-items: center;
  color: color(primary-text);
  border: 1px solid transparent;

  transition: all 200ms ease-in-out;
  font-weight: normal;
  color: color(navigation-default-text);

  &:hover {
    background: color(navigation-hover-background);
    color: color(navigation-selecting-text);
  }
}

.account-switcher__link--active {
  background: color('navigation-selected-background');
  color: color('navigation-selected-text');
  pointer-events: none;
}

.account-switcher__link--updates {
  color: color(error);
}

.spinner-icon {
  opacity: 0;
}

.show-icon {
  opacity: 1;
}

.separator {
  margin: 0.5em 0;
}
</style>
